import {
  Button,
  ChipTab,
  EmptyState,
  ListPanel,
  ProviderList,
  ProviderListItem,
  ProviderListItemSkeleton,
  SearchField,
  Stack,
  Tag,
} from '@all-manager/design-system-v2';
import NoProvidersIcon from '../../assets/no-providers.svg?react';
import { ProviderTag } from '../../types';
import ProviderMenuAction from "../ProviderMenuAction/ProviderMenuAction";
import ProvidersPagination from "../ProvidersPagination/ProvidersPagination";
import { useProvidersList } from "./useProvidersList";

const SKELETON_ROWS = [0, 1, 2, 3];

const filterScrollContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  overflowX: 'auto',
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 16,
  paddingRight: 16,
  marginLeft: -16,
  marginRight: -16,
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
};

export default function ProvidersList() {
  const hook = useProvidersList();

  if (hook.isEmpty) {
    return (
      <EmptyState
        title="Aucun prestataire"
        subtitle="La liste des prestataires apparaîtra ici."
        image={<NoProvidersIcon />}
      />
    );
  }

  const showToolbar = hook.showSearch || hook.showFilters;

  return (
    <ListPanel
      toolbar={
        showToolbar ? (
          <Stack spacing={1}>
            {hook.showSearch ? (
              <SearchField
                value={hook.searchTerm}
                onChange={hook.setSearchTerm}
                label="Rechercher un prestataire"
                placeholder="Nom, activité ou adresse"
              />
            ) : null}

            {hook.showFilters ? (
              <div style={filterScrollContainerStyle}>
                <ChipTab
                  label="Tous"
                  selected={hook.selectedTagIds.length === 0 && !hook.underContractOnly}
                  onClick={hook.handleClearFilters}
                />
                <ChipTab
                  label="Sous contrat"
                  selected={hook.underContractOnly}
                  onClick={hook.handleToggleUnderContract}
                />
                {hook.tags.map((tag: ProviderTag) => (
                  <ChipTab
                    key={tag.id}
                    label={tag.name}
                    selected={hook.selectedTagIds.includes(tag.id)}
                    onClick={() => hook.handleToggleTag(tag.id)}
                  />
                ))}
              </div>
            ) : null}
          </Stack>
        ) : undefined
      }
      footer={
        hook.showPagination ? (
          <ProvidersPagination
            count={hook.total}
            page={hook.page}
            rowsPerPage={hook.pageSize}
            onPageChange={hook.setPage}
            onRowsPerPageChange={hook.handlePageSizeChange}
          />
        ) : undefined
      }
    >
      {hook.isLoading ? (
        <ProviderList aria-label="Prestataires" aria-busy>
          {SKELETON_ROWS.map((row) => (
            <ProviderListItemSkeleton key={row} />
          ))}
        </ProviderList>
      ) : hook.hasNoResult ? (
        <EmptyState
          title="Aucun résultat"
          subtitle={hook.noResultSubtitle}
          action={
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={hook.handleClearAllFilters}
            >
              Effacer les filtres
            </Button>
          }
        />
      ) : (
        <ProviderList aria-label="Prestataires">
          {hook.providers.map((provider) => (
            <ProviderListItem
              key={provider.id}
              name={provider.name}
              description={provider.description}
              address={provider.address}
              tags={
                provider.hasActiveContract ||
                (provider.tags && provider.tags.length > 0) ? (
                  <>
                    {provider.hasActiveContract ? (
                      <Tag
                        label="Sous contrat"
                        color="done"
                        variantIcon="state"
                      />
                    ) : null}
                    {provider.tags?.map((tag: ProviderTag) => (
                      <Tag
                        key={tag.id}
                        label={tag.name}
                        color="default"
                        variantIcon="label"
                      />
                    ))}
                  </>
                ) : undefined
              }
              href={hook.getProviderHref(provider.id)}
              onClick={hook.getProviderClickHandler(provider.id)}
              action={
                hook.isAdmin ? (
                  <ProviderMenuAction provider={provider} />
                ) : undefined
              }
            />
          ))}
        </ProviderList>
      )}
    </ListPanel>
  );
}
